import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Row, Col, Typography, Empty } from 'antd';
import './Campus.less';

const { Title, Paragraph } = Typography;

interface CampusLifeItem {
  id: number;
  title: string;
  content: string;
  imageUrl: string;
  category: string;
  date: string;
}

const Campus: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<CampusLifeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('全部');

  // 生成校园生活数据
  useEffect(() => {
    // 模拟API请求延迟
    const timer = setTimeout(() => {
      const generatedItems: CampusLifeItem[] = generateCampusLifeData();
      setItems(generatedItems);
      setLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // 生成36条校园生活数据
  const generateCampusLifeData = (): CampusLifeItem[] => {
    const categories = ['学术活动', '文化艺术', '体育运动', '社团活动', '志愿服务', '校园环境'];
    const titles = [
      '校园春季运动会盛大开幕',
      '学生社团招新活动如火如荼',
      '校园音乐节精彩上演',
      '科技创新大赛获奖作品展',
      '校园图书馆新馆开放',
      '毕业季摄影展温馨开展',
      '校园美食节热闹非凡',
      '校园马拉松比赛圆满结束',
      '学生科技社团成果展示',
      '校园话剧社年度大戏首演',
      '校园篮球联赛激烈进行',
      '绿色校园植树活动',
      '校园英语角活动丰富多彩',
      '校园辩论赛巅峰对决',
      '校园歌手大赛总决赛',
      '校园创客空间正式启用',
      '校园志愿者招募启动',
      '校园文化节系列活动',
      '校园消防安全演练',
      '校园心理健康讲座',
      '校园迎新晚会精彩纷呈',
      '校园计算机技能大赛',
      '校园书法比赛优秀作品展示',
      '校园环保创意大赛',
      '校园模拟联合国大会',
      '校园电竞大赛激情开赛',
      '校园创业分享会',
      '校园爱心捐赠活动',
      '校园绿植领养活动',
      '校园冬季长跑活动',
      '校园机器人比赛',
      '校园诗歌朗诵比赛',
      '校园微电影大赛',
      '校园科研成果展',
      '校园新生篮球赛',
      '校园新年音乐会',
      '校园读书月活动启动'
    ];

    return Array.from({ length: 36 }, (_, index) => ({
      id: index + 1,
      title: titles[index],
      content: `这是关于${titles[index]}的详细描述。活动在羊小羊校园内举行，吸引了众多师生参与。活动现场氛围热烈，同学们积极互动，展现了羊小羊学子的青春活力和团队精神。通过此类活动，丰富了校园文化生活，促进了同学之间的交流与合作。`,
      imageUrl: `https://picsum.photos/seed/campus${index + 1}/600/400`,
      category: categories[index % categories.length],
      date: `${2024}-${String((index % 12) + 1).padStart(2, '0')}-${String((index % 28) + 1).padStart(2, '0')}`
    }));
  };



  // 过滤显示的内容
  const filteredItems = activeCategory === '全部' 
    ? items 
    : items.filter(item => item.category === activeCategory);

  // 获取所有分类
  const categories = ['全部', ...Array.from(new Set(items.map(item => item.category)))];

  return (
    <div className="campus-container">
      <Title level={2} className="campus-title">校园生活</Title>
      
      {/* 分类筛选 */}
      <div className="category-filter">
        {categories.map(category => (
          <button
            key={category}
            className={`category-btn ${activeCategory === category ? 'active' : ''}`}
            onClick={() => setActiveCategory(category)}
          >
            {category}
          </button>
        ))}
      </div>

      {/* 图文混排内容 */}
      <Row gutter={[16, 24]} className="campus-content">
        {loading ? (
          <Col span={24}>
            <div className="loading-container">
              <Empty description="加载中..." />
            </div>
          </Col>
        ) : filteredItems.length > 0 ? (
          filteredItems.map((item, index) => (
            <Col xs={24} sm={12} md={8} key={item.id}>
              <Card
                hoverable
                className="campus-card"
                cover={
                  <div 
                    className="card-image-container" 
                    onClick={() => navigate(`/campus/${item.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <img 
                      alt={item.title} 
                      src={item.imageUrl} 
                      className="card-image"
                    />
                    <div className="image-overlay">
                      <span className="category-tag">{item.category}</span>
                    </div>
                  </div>
                }
              >
                <div className="card-content">
                  <div className="card-meta">
                    <span className="card-date">{item.date}</span>
                  </div>
                  <Title 
                    level={4} 
                    className="card-title" 
                    onClick={() => navigate(`/campus/${item.id}`)}
                    style={{ cursor: 'pointer', marginBottom: '12px' }}
                  >
                    {item.title}
                  </Title>
                  <Paragraph className="card-text" ellipsis={{ rows: 3 }}>
                    {item.content}
                  </Paragraph>
                  <button 
                    className="read-more-btn" 
                    onClick={() => navigate(`/campus/${item.id}`)}
                  >
                    查看详情
                  </button>
                </div>
              </Card>
            </Col>
          ))
        ) : (
          <Col span={24}>
            <Empty description="暂无相关内容" />
          </Col>
        )}
      </Row>
    </div>
  );
};

export default Campus;